import { NIL } from 'uuid';
import { User } from './user';
import { Vendor } from './contactInfo';
import { ProposalItem } from './proposal';
import { PurchaseOrderItem } from './purchaseOrder';
import { WorkOrderItem } from './workOrder';
import {InvoiceItem} from "./invoice";
import {BillItems} from "./bill";

// item 186 — per-SKU commission payout reasons as a [Flags] bitmask (mirrors the backend
// CommissionPayoutType). One catalog item can pay MULTIPLE reasons (RNWY-SYS CONFIG pays
// all four; the other Press Play SKUs pay IO Sales + Sales PO). A ledger row's sourceType
// carries the single reason it came from. ProjectLead keeps value 2 (legacy data stays valid).
export enum CommissionPayoutType {
  None = 0,
  ProjectLead = 2,
  IOSales = 4,
  SalesPO = 8,
  DevelopingPartner = 16,
}

export const COMMISSION_PAYOUT_LABELS: Record<CommissionPayoutType, string> = {
  [CommissionPayoutType.None]: 'None',
  [CommissionPayoutType.ProjectLead]: 'Project lead',
  [CommissionPayoutType.IOSales]: 'IO Sales (outside rep)',
  [CommissionPayoutType.SalesPO]: 'Sales PO (inside rep)',
  [CommissionPayoutType.DevelopingPartner]: 'Developing partner',
};

// item 39 — which document types a catalog Item may be placed on. A [Flags] bitmask
// (mirrors the backend DocumentAvailability enum). Default = ALL_AVAILABILITY.
export enum DocumentAvailability {
  None = 0,
  Proposal = 1,
  WorkOrder = 2,
  SalesOrder = 4,
  Invoice = 8,
  PurchaseOrder = 16,
  Bill = 32,
}

export const ALL_AVAILABILITY = 63; // every bit set

// the individual toggleable doc types, in form/display order
export const AVAILABILITY_OPTIONS: { value: DocumentAvailability; label: string }[] = [
  { value: DocumentAvailability.Proposal, label: 'Proposals' },
  { value: DocumentAvailability.WorkOrder, label: 'Work Orders' },
  { value: DocumentAvailability.SalesOrder, label: 'Sales Orders' },
  { value: DocumentAvailability.Invoice, label: 'Invoices' },
  { value: DocumentAvailability.PurchaseOrder, label: 'Purchase Orders' },
  { value: DocumentAvailability.Bill, label: 'Bills' },
];

// bitmask helpers (item 39) — the only place we do bitwise ops on the availability mask
/* eslint-disable no-bitwise */
export const hasAvailability = (mask: number, bit: DocumentAvailability): boolean => (mask & bit) === bit;
export const toggleAvailability = (mask: number, bit: DocumentAvailability): number => (
  hasAvailability(mask, bit) ? mask & ~bit : mask | bit
);
/* eslint-enable no-bitwise */

// item 289 — the 4 selectable per-SKU payout reasons (any combination; None excluded)
export const PAYOUT_REASON_FLAGS: CommissionPayoutType[] = [
  CommissionPayoutType.IOSales,
  CommissionPayoutType.SalesPO,
  CommissionPayoutType.ProjectLead,
  CommissionPayoutType.DevelopingPartner,
];
/* eslint-disable no-bitwise */
export const hasPayout = (mask: number, flag: CommissionPayoutType): boolean => (mask & flag) === flag;
export const togglePayout = (mask: number, flag: CommissionPayoutType): number => (
  hasPayout(mask, flag) ? mask & ~flag : mask | flag
);
/* eslint-enable no-bitwise */

export const isAvailableOn = (availability: number | undefined, doc: DocumentAvailability): boolean => {
  // an unset/None mask defaults to available everywhere (matches the backend coalesce)
  const mask = availability === undefined || availability === DocumentAvailability.None ? ALL_AVAILABILITY : availability;
  return hasAvailability(mask, doc);
};

export interface Item {
  id: string;
  sku: string;
  brand: string;
  description: string;
  vendorId?: string;
  costEach: number;
  retailEach: number;
  // Chad's canonical model (2026-07-14) — the stored MSRP (hardware) / SRP (services): the
  // suggested resale, source of truth; the MSRP-markup multiplier derives from msrp ÷ grp.
  // Display/seed only — never drives document totals (grp price is the binding sell price).
  msrpEach?: number;
  commissionPayout?: CommissionPayoutType; // item 30/39
  commissionReasonIds?: string; // item 315 — CSV of custom CommissionReason ids
  quickBooksSku?: string; // item 943 — QB AR export SKU code (services); blank = generic part item
  isHardware?: boolean; // item 78
  isTaxable?: boolean; // item 178 — default taxability inherited by document lines
  distributorEnabled?: boolean; // item 39
  distributorEach?: number; // item 39
  groupMarkup?: number | null; // item 234 — group markup over dealer cost (null = company default)
  availability?: number; // item 39 — DocumentAvailability bitmask
  quantityOnHand?: number; // item 127
  lowStockThreshold?: number | null; // item 127
  isArchive?: boolean; // item 150
  isProtected?: boolean; // item 186 — system payout SKU: never archived/deleted, payout fixed
  systemKey?: string; // item 186 — hidden role tag for the seeded protected SKUs
  vendor?: Vendor;
  lastModifiedBy?: User;
  lastModifiedDate: string;
}

// item 234 (reworked) — the GROUP price over the dealer cost (CostEach): the item's
// own markup wins, a null/0 markup falls back to the company default. This is the
// binding sell price suggested on proposals/sales orders + previewed on the item form.
// item 429 — a stored markup of exactly 1 is an import artifact, not a real price
// call: GROUP PRICE is the CUSTOMER price and must never silently equal cost (Chad's
// $372.76 = $372.76 line). Only markups > 1 are honored; 1/0/null → company default.
export const groupPrice = (
  item: Pick<Item, 'costEach' | 'groupMarkup'>, defaultMarkup: number,
): number => {
  const m = item.groupMarkup && item.groupMarkup > 1 ? item.groupMarkup : defaultMarkup;
  return Math.round((item.costEach ?? 0) * m * 100) / 100;
};

// item 236 — the SRP / MSRP customer-facing SUGGESTION over the dealer cost. These are
// display-only (never drive totals): SRP shows on proposals, MSRP on sales orders.
// Mirror groupPrice's rounding. Item-level overrides aren't modeled yet → company default.
export const srpPrice = (
  item: Pick<Item, 'costEach'>, defaultSrpMarkup: number,
): number => Math.round((item.costEach ?? 0) * defaultSrpMarkup * 100) / 100;

export const msrpPrice = (
  item: Pick<Item, 'costEach'>, defaultMsrpMarkup: number,
): number => Math.round((item.costEach ?? 0) * defaultMsrpMarkup * 100) / 100;

// Chad's canonical model (2026-07-14) — the binding line price is the item's entered
// GROUP PRICE (RetailEach), for HARDWARE and SERVICES alike. This SUPERSEDES item 434's
// hardware "born marked-up" (cost × groupMarkup): the group price is now entered directly
// on the item (DISTRO → GRP MRK → GRP PRICE), so a line inherits it verbatim rather than
// recomputing from cost.
export const bindingLinePrice = (
  item: Pick<Item, 'retailEach'>,
): number => Math.round((item.retailEach ?? 0) * 100) / 100;

// Chad's canonical model — the customer-facing resale SUGGESTION (SRP on proposals /
// MSRP on sales orders). The item's STORED MsrpEach wins when set; otherwise fall back to
// the legacy compute (hardware = group price × the SRP/MSRP markup; services = group price
// × the item's group markup, item override first, company default otherwise).
export const suggestedResalePrice = (
  item: Pick<Item, 'costEach' | 'groupMarkup' | 'retailEach' | 'isHardware' | 'msrpEach'>,
  defaultGroupMarkup: number,
  defaultSuggestionMarkup: number,
): number => {
  if (item.msrpEach && item.msrpEach > 0) return Math.round(item.msrpEach * 100) / 100;
  // legacy fallback for items without a stored MSRP (item 434 math):
  if (item.isHardware) {
    return Math.round(groupPrice(item, defaultGroupMarkup) * defaultSuggestionMarkup * 100) / 100;
  }
  const m = item.groupMarkup && item.groupMarkup > 1 ? item.groupMarkup : defaultGroupMarkup; // item 429
  return Math.round((item.retailEach ?? 0) * m * 100) / 100;
};

// item 127 — at or below the (optional) reorder point = low stock
export const isLowStock = (item: Pick<Item, 'quantityOnHand' | 'lowStockThreshold'>): boolean => (
  item.lowStockThreshold !== undefined && item.lowStockThreshold !== null
  && (item.quantityOnHand ?? 0) <= item.lowStockThreshold
);

// item 290 — a Purchase Order line buys at one of two cost tiers. DEALER = the item's
// base cost (CostEach); DISTRIBUTOR = the optional lower distribution price (item 39).
// The PO line carries the chosen tier so the per-line selector can swap the price.
export type PoPriceTier = 'dealer' | 'distributor';

export interface PoTierCosts {
  dealer: number | undefined;
  distributor: number | undefined; // undefined = this item has no distributor tier
}

// the two tiers a catalog item exposes to a PO line (distributor only when enabled + priced)
export const poTierCosts = (
  item: Pick<Item, 'costEach' | 'distributorEnabled' | 'distributorEach'>,
): PoTierCosts => ({
  dealer: item.costEach,
  distributor: item.distributorEnabled && item.distributorEach ? item.distributorEach : undefined,
});

// default tier for a freshly-added line: distributor when the item carries one, else dealer
export const defaultPoTier = (tiers: PoTierCosts): PoPriceTier => (
  tiers.distributor != null ? 'distributor' : 'dealer'
);

// the cost for a chosen tier; falls back to dealer when the distributor tier is missing,
// so selecting a tier can never strand the line at an undefined price
export const poTierCost = (tier: PoPriceTier, tiers: PoTierCosts): number | undefined => (
  tier === 'distributor' && tiers.distributor != null ? tiers.distributor : tiers.dealer
);

// the tier a line currently reflects: its explicit choice wins; otherwise infer from the
// price (matches the distributor tier? distributor : dealer)
export const resolvePoTier = (
  explicit: PoPriceTier | undefined, costEach: number | undefined, tiers: PoTierCosts,
): PoPriceTier => {
  if (explicit) return explicit;
  if (tiers.distributor != null && costEach === tiers.distributor) return 'distributor';
  return 'dealer';
};
export interface ItemDto {
  index: Item[];
  length: number;
}

export const getEmptyItem = (): Item => ({
  id: NIL,
  sku: '',
  brand: '',
  description: '',
  vendorId: undefined,
  costEach: 0,
  retailEach: 0,
  msrpEach: 0,
  commissionPayout: CommissionPayoutType.None,
  isHardware: false, // item 78
  isTaxable: true, // item 178 — goods default taxable
  distributorEnabled: false, // item 39
  distributorEach: 0, // item 39
  groupMarkup: null, // item 234 — use company default
  availability: ALL_AVAILABILITY, // item 39 — available everywhere by default
  quantityOnHand: 0, // item 127
  lowStockThreshold: null, // item 127
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});

export const fromProposalItem = (proposalItem: ProposalItem): Item => ({
  id: NIL,
  sku: proposalItem.sku,
  brand: proposalItem.brand,
  description: proposalItem.description,
  vendorId: proposalItem.vendorId,
  costEach: proposalItem.costEach,
  retailEach: proposalItem.retailEach,
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});

export const fromInvoiceItem = (invoiceItem: InvoiceItem): Item => ({
  id: NIL,
  sku: invoiceItem.sku,
  brand: invoiceItem.brand,
  description: invoiceItem.description,
  vendorId: invoiceItem.vendorId,
  costEach: invoiceItem.costEach,
  retailEach: invoiceItem.retailEach,
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});

export const fromBillItem = (billItem: BillItems): Item => ({
  id: NIL,
  sku: billItem.sku,
  brand: billItem.brand,
  description: billItem.description,
  vendorId: billItem.vendorId,
  costEach: billItem.costEach,
  retailEach: billItem.retailEach,
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});

export const fromWorkOrderItem = (workOrderItem: WorkOrderItem): Item => ({
  id: NIL,
  sku: workOrderItem.sku,
  brand: workOrderItem.brand,
  description: workOrderItem.description,
  vendorId: workOrderItem.vendorId,
  costEach: workOrderItem.costEach,
  retailEach: workOrderItem.retailEach,
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});

export const fromPurchaseOrderItem = (purchaseOrderItem: PurchaseOrderItem): Item => ({
  id: NIL,
  sku: purchaseOrderItem.sku,
  brand: purchaseOrderItem.brand,
  description: purchaseOrderItem.description,
  costEach: purchaseOrderItem.costEach,
  vendorId: purchaseOrderItem.vendorId,
  // item 554 — carry the PO line's group price + hardware flag back so saving it to the
  // catalog keeps its three-tier pricing (was hard-coded to 0 before POs tracked retail)
  retailEach: purchaseOrderItem.retailEach ?? 0,
  isHardware: purchaseOrderItem.isHardware,
  vendor: undefined,
  lastModifiedBy: undefined,
  lastModifiedDate: '',
});
